import { useEffect, useRef, useState } from 'react'

/**
 * Drag/drop + paste handlers for the chat composer's drop region.
 *
 * Lives in its own module (not next to `AttachmentComposer`) so the
 * composer file can stay a pure component module — react-refresh's lint
 * forbids mixing hook exports with component exports.
 *
 * Returns a set of React event handlers (designed to be spread onto the
 * composer wrapper element) plus an `isDragging` flag the caller uses to
 * toggle the visible drop overlay.
 */
export interface AttachmentDropTargetOptions {
  onAddFiles: (
    files: readonly File[],
    options?: { source?: 'paste' | 'screenshot' | 'file' },
  ) => void
  disabled?: boolean
}

export interface AttachmentDropHandlers {
  onDragEnter: (e: React.DragEvent<HTMLElement>) => void
  onDragOver: (e: React.DragEvent<HTMLElement>) => void
  onDragLeave: (e: React.DragEvent<HTMLElement>) => void
  onDrop: (e: React.DragEvent<HTMLElement>) => void
  onPaste: (e: React.ClipboardEvent<HTMLElement>) => void
}

export function useAttachmentDropTarget({
  onAddFiles,
  disabled,
}: AttachmentDropTargetOptions): {
  isDragging: boolean
  handlers: AttachmentDropHandlers
} {
  const [isDragging, setIsDragging] = useState(false)
  // Count enter/leave events so nested children (textarea, button) don't
  // flicker the overlay every time the cursor crosses an internal boundary.
  const depthRef = useRef(0)

  // Reset depth + overlay if drag is cancelled while we have a stale count.
  useEffect(() => {
    const onWindowDragEnd = () => {
      depthRef.current = 0
      setIsDragging(false)
    }
    window.addEventListener('dragend', onWindowDragEnd)
    window.addEventListener('drop', onWindowDragEnd)
    return () => {
      window.removeEventListener('dragend', onWindowDragEnd)
      window.removeEventListener('drop', onWindowDragEnd)
    }
  }, [])

  const reset = () => {
    depthRef.current = 0
    setIsDragging(false)
  }

  const handlers: AttachmentDropHandlers = {
    onDragEnter: (e) => {
      if (disabled) return
      const hasFiles = e.dataTransfer?.types?.includes('Files') ?? false
      if (!hasFiles) return
      e.preventDefault()
      depthRef.current += 1
      setIsDragging(true)
    },
    onDragOver: (e) => {
      if (disabled) return
      const hasFiles = e.dataTransfer?.types?.includes('Files') ?? false
      if (hasFiles) {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'copy'
      }
    },
    onDragLeave: (e) => {
      if (disabled) return
      e.preventDefault()
      depthRef.current = Math.max(0, depthRef.current - 1)
      if (depthRef.current === 0) setIsDragging(false)
    },
    onDrop: (e) => {
      if (disabled) {
        reset()
        return
      }
      const files = Array.from(e.dataTransfer?.files ?? [])
      e.preventDefault()
      reset()
      if (files.length === 0) return
      onAddFiles(files, { source: 'file' })
    },
    onPaste: (e) => {
      if (disabled) return
      const items = Array.from(e.clipboardData?.items ?? [])
      const files: File[] = []
      for (const item of items) {
        if (item.kind === 'file') {
          const file = item.getAsFile()
          if (file) files.push(file)
        }
      }
      if (files.length === 0) return
      // Only intercept paste when the user actually pasted a file — keep
      // the text-paste path working untouched.
      e.preventDefault()
      onAddFiles(files, { source: 'paste' })
    },
  }

  return { isDragging, handlers }
}
